import * as z from 'zod';

import { zodDateOrString } from '../../utils';

// Ginnie Mae RFS reports can include several different row types: Pool rows, Loan rows, and Sensitive data rows.
// (There are also header and trailer rows, but those will be generated at report export time). Since the schema
// is different for all three rows, we will represent GinnieMaeRFSV1 as the union of those types.

// Todo: add this schema when we finish investor pool management
const GinnieMaeRFSPoolRow = z.object({
  rowType: z.literal('P').describe('literal'),
});

// Todo: add this schema
const GinnieMaeRFSSensitiveRow = z.object({
  rowType: z.literal('S').describe('literal'),
});

export const GinnieMaeRFSLoanRow = z.object({
  rowType: z.literal('L').describe('literal'),
  /**
   *  A number assigned by Ginnie Mae, which uniquely identifies this loan
   */
  ginnieMaeLoanId: z.string().describe('string'),
  /**
   * The Ginnie Mae Pool identifier assigned by Ginnie Mae at pooling to the pool/loan package.
   */
  poolId: z.string().describe('string'),
  /**
   *  A code which denotes the government agency associated with this loan.
   */
  loanType: z.enum(['FHA', 'FH1', 'FMF', 'RHS', 'RMF', 'PIH', 'VAG', 'VAV']).describe('enum'),
  /**
   * The Case Number to be reported per Ginnie Mae APM 02-17 (e.g., FHA, RHS, PIH, or VA). It must be the
   * same Case Number that was reported (on the Schedule of Pooled Mortgages) to Ginnie Mae’s electronic
   * pooling application at the time of pool origination. All Case Numbers must contain 15 character positions.
   */
  caseNumber: z.string().describe('string'),
  /**
   * The number assigned by the Issuer to uniquely identify the loan to their internal system.
   */
  issuerLoanID: z.string().describe('string'),
  /**
   * The date in which the first monthly installment payment was due in accordance with the mortgage/note. For
   * modified loans, report the first payment date of the re-amortized loan; the same date reported at pooling.
   */
  firstPaymentDate: zodDateOrString.describe('date'),
  /**
   * The maturity date of this loan in accordance with mortgage/note. For modified loans, report
   * the maturity date of the re-amortized loan; the same date reported at pooling.
   */
  loanMaturityDate: zodDateOrString.describe('date'),
  /**
   * The interest rate of the loan. For modified loans, report the loan interest rate of the reamortized loan;
   * the same rate reported at pooling.
   */
  loanInterestRate: z.number().describe('interestRate'),
  /**
   * Original Principal Balance, the dollar amount of the original loan principal balance as stated on the
   * mortgage note. For modified loans, report the original OPB of the loan per the “original” note; not the
   * modified OPB.
   */
  loanOPB: z.number().describe('currency'),
  /**
   * The current fixed installment constant of principal and interest payments for this loan. For modified
   * loans, report the monthly FIC (principal and interest) of the re-amortized loan; the same value reported at pooling.
   */
  loanFIC: z.number().describe('currency'),
  /**
   * The loan amortization schedule date of the last installment payment received. If the borrower has not made any
   * payment to the loan and if there is no Last Installment Paid Date, do not report a value in this field
   * (report blank in this field).
   */
  lpiDate: zodDateOrString.describe('date'),
  /**
   * A boolean field that indicates whether the loan is in foreclosure. In the actual report this is a string 'Y' or 'N'.
   */
  inForeclosure: z.boolean().describe('boolean'),
  /**
   *  The total interest portions of delinquent installments due for this loan. This is the interest portion
   * of unpaid installments that are delinquent (or in foreclosure) as of the end of the reporting period.
   */
  delinquentInterest: z.number().describe('currency'),
  /**
   * The total principal portions of delinquent installments due for this loan. This is the principal portion
   * of unpaid installments that are delinquent (or in foreclosure) as of the end of the reporting period.
   */
  delinquentPrincipal: z.number().describe('currency'),
  /**
   * The total interest portions of prepaid installments collected for this loan. Report interest portion of
   * monthly installments that have been collected where the due dates of the installments are later than the
   * current reporting month.
   */
  prepaidInterest: z.number().describe('currency'),
  /**
   * The total principal portions of prepaid installments collected for this loan. Report the principal portion of
   * monthly installments that have been collected where the due dates of the installments are later than the
   * current reporting month.
   */
  prepaidPrincipal: z.number().describe('currency'),
  /**
   * The amount of all installment interest collected for this loan this reporting period. This includes the
   * scheduled payment due this month; delinquent installments collected, as well as prepaid installments. Report
   * all monthly installments of interest collected (actually received) on pooled loans during the reporting month.
   */
  installInterest: z.number().describe('currency'),
  /**
   * The amount of all installment principal collected for this loan this reporting period. This
   * includes the scheduled payment due this month; delinquent installments collected, as well as prepaid installments.
   * Enter all monthly installments of principal collected (actually received) on pooled loans during the reporting month.
   * Do not include curtailments.
   */
  installPrincipal: z.number().describe('currency'),
  /**
   * The dollar amount of non-scheduled additional principal collected for this loan this reporting
   * period. Report principal amounts credited to the mortgage loan this reporting period that is “in addition” to monthly
   * installments. Report any type of curtailment of principal in this field including items such as partial claims. Do not
   * report liquidation-in-full in this field.
   */
  curtailment: z.number().describe('currency'),
  /**
   * A signed field. Dollar amount of adjustment to the loan interest collected. Reasons for adjustments may include:
   * 1) Curtailment interest adjustment, 2) Reversal of an installment payment because the check bounced, 3) Corrections
   * to mistakes made in prior reporting.
   */
  adjustInterest: z.number().describe('currency'),
  /**
   * A signed field. Adjustments to the loan principal balance: The dollar amount of adjustment to the unpaid principal
   * balance of the loan. Reasons for adjustments may include: 1) Construction Draws, 2) New loan substituted for a
   * liquidated loan 3) Reversal of an installment payment because the check bounced, 4) Corrections to mistakes made
   * in prior reporting. Not for curtailments.
   */
  netAdjustUPB: z.number().describe('currency'),
  /**
   * A signed field. The UPB of the loan this reporting period, as of the close of the Issuer’s
   * reporting cutoff for the reporting period. The value reported should not include amounts for unsecuritized
   * construction loan draws. For loans being liquidated, report the amount of the principal balance remaining on the
   * loan after application of the last installment received from the mortgagor prior to the liquidation.
   */
  actualLoanUPB: z.number().describe('currency'),
  /**
   * For loan liquidation, the date in which the loan was removed from the Ginnie Mae pool. Format
   * MMDDYYYY. Liquidation is any transaction that reduces the unpaid principal balance of a loan to zero in the
   * reporting period.
   */
  removalDate: zodDateOrString.optional().describe('date'),
  /**
   * A code that denotes the reason why the loan was removed from the Ginnie Mae pool.
   * Possible values are:
   * - 1: Mortgagor Payoff
   * - 2: Buyout of Delinquent Loan
   * - 3: Foreclosure with or without Claim Payment
   * - 4: Loss Mitigation
   * - 5: Substitution
   * - 6: Other Removal
   * - 7: Special Assistance
   */
  removalReasonCode: z.enum(['1', '2', '3', '4', '5', '6', '7']).optional().describe('enum'),
  /**
   * The dollar amount of the interest, due the pool.
   *
   * **Willow notes**: only the amount due to the investor, not the total interest collected. Total interest paid by
   * the borrower may be larger than this number.
   */
  liquidationInterestDue: z.number().optional().describe('currency'),
  /**
   * The sum of each of the installment principal amounts remitted.
   */
  liquidationPrincipalRemitted: z.number().optional().describe('currency'),
  /**
   * A signed field. The remaining principal balance of the loan at liquidation
   */
  liquidationPrincipalBalance: z.number().optional().describe('currency'),
  /**
   * A signed field. The balance of taxes and insurance funds in escrow or impound account, for the loan, as of
   * the Issuers reporting cutoff date. This field applies to Single-Family loans only and is not required for
   * Multifamily loans.
   */
  loanTaxesInsuranceBalance: z.number().optional().describe('currency'),
  /**
   * The dollar amount of the current Unpaid Principal Balance of the loan amortized through the
   * month following the current reporting period. The calculated scheduled (per the amortization schedule) unpaid
   * principal balance of the mortgage as of the end of the reporting period. For a current loan the Scheduled UPB is
   * generally equal to the actual principal balance of the mortgage at the close of the report period; amortized one month
   * beyond the report period (e.g. For a July Report Period, the scheduled UPB is the August balance). Prepaid or
   * delinquent loans must be “normalized/amortized” to what would be the current UPB for the report period (and then
   * amortized one month beyond the report period). For example, July Report Period a delinquent loan must be brought
   * forward to the current end of July balance, then amortized one month to the August balance. The balance can be
   * zero, but not negative. The Scheduled UPB is a calculated amount, by the Issuer, for reporting to RFS.
   */
  scheduledUPB: z.number().describe('currency'),
  /**
   * The calculated scheduled (per the amortization schedule) monthly
   * principal amount for the mortgage as of the end of the reporting period. The Scheduled Monthly Principal Amount is
   * equal to the principal component of the scheduled payment due on the mortgage amortized one month beyond the
   * report period for a current loan. Prepaid or delinquent loans must be “normalized/amortized” to the Scheduled
   * Monthly Principal Amount (to the current period and then amortized one month beyond the report period (consistent
   * with Field 30 Scheduled UPB calculations)).
   */
  scheduledMonthlyPrincipalAmount: z.number().describe('currency'),
  /**
   * The calculated scheduled (per the amortization schedule) monthly interest
   * amount for the mortgage as of the end of the reporting period. The Scheduled Monthly Interest Amount is generally
   * equal to the interest due on the scheduled payment due on the mortgage amortized to one month beyond the report
   * period for a current loan. Prepaid or delinquent loans must be “normalized/amortized” to the Scheduled Monthly
   * Interest Amount (to the current period and then amortized one month beyond the report period (consistent with Field
   * 30 Scheduled UPB calculations)).
   */
  scheduledMonthlyInterestAmount: z.number().describe('currency'),
  /**
   * The dollar amount of servicing fee collected on the mortgage, for the
   * report period. A dollar amount field, must be greater than zero (otherwise spaces or zeros per existing RFS
   * instructions). Report the Gross Servicing Fee collected on the loan as posted to the servicing system (net as of
   * month end, if there were multiple postings). Do not recalculate for reporting.
   */
  grossServiceFeeAmountCollected: z.number().describe('currency'),
  /**
   * The actual date the last scheduled installment payment was received the posting date for the installment
   * reported in `lpiDate`.
   */
  actualPaymentDate: zodDateOrString.optional().describe('date'),
  /**
   * A code indicating the type of curtailment reported. A value must be reported when
   * a Curtailment is reported in `curtailment`. Possible values are:
   * - 1: Borrower Funds Curtailment
   * - 2: Claim Funds Curtailment
   * - 3: Both Borrower Funds Curtailment and Claim Funds Curtailments
   * - 4: Other Funds Curtailment (Other Principal Reduction Funds that flow through the loan; eg. HAF or other
   *      Incentive funds; a combination of all codes, use Other.)
   */
  curtailmentPrincipalCode: z.enum(['1', '2', '3', '4']).optional().describe('enum'),
  /**
   * The new "to be" interest rate of the ARM loan. It must be a greater than zero, if reported.
   */
  armProspectiveInterestRate: z.number().optional().describe('interestRate'),
  /**
   * The new “to be” monthly P&I payment amount of the ARM loan.
   */
  armProspectiveMonthlyPI: z.number().optional().describe('currency'),
  /**
   *  The effective date of the ARM adjustment; the scheduled installment due date of the new "to be" P&I on the loan.
   * This is the date on which the interest rate adjustment goes into effect. It must be a valid date value, if reported.
   */
  armAdjustmentEffectiveDate: zodDateOrString.optional().describe('date'),
});

export type GinnieMaeRFSLoanRow = z.infer<typeof GinnieMaeRFSLoanRow>;

export const GinnieMaeRFSV1 = z.object({
  version: z.literal('1').describe('version'),
  row: z.discriminatedUnion('rowType', [GinnieMaeRFSPoolRow, GinnieMaeRFSLoanRow, GinnieMaeRFSSensitiveRow]),
});
export type GinnieMaeRFSV1 = z.infer<typeof GinnieMaeRFSV1>;

export const GinnieMaeRFS = GinnieMaeRFSV1;
export type GinnieMaeRFS = z.infer<typeof GinnieMaeRFS>;
// Make `GinnieMaeRFS` a discriminated union on 'version' when changes happen
// export const GinnieMaeRFSV2 = z.object({
//   version: z.literal('2'),
//   foo: z.string(),
// });
// export const GinnieMaeRFS = z.discriminatedUnion('version', [GinnieMaeRFSV1, GinnieMaeRFSV02012025]);
